import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, CalendarCheck, CheckCircle2, LockKeyhole, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  buildOpportunityPayload,
  EntryPointConfig,
  OpportunityPayload,
} from "@/lib/opportunityPayload";
import {
  asSubmittedResponse,
  submitOperatorOpportunity,
  type OperatorIntakeResult,
} from "@/lib/operatorIntake";
import { trackEvent } from "@/lib/analytics";
import { buildAppRescueDiagnosticCta } from "@/lib/appRescueBooking";
import {
  buildIntakeEventProps,
  buildIntakeSubmissionProps,
  trackAppRescueFunnelStep,
} from "@/lib/publicFunnel";

function newSubmissionId(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID();
  }
  return `sub-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function textLengthBucket(value: string | undefined): string {
  const length = value?.trim().length || 0;
  if (length >= 240) return "long";
  if (length >= 120) return "medium";
  if (length >= 40) return "short";
  if (length > 0) return "very_short";
  return "empty";
}

interface OpportunityIntakeFormProps {
  config: EntryPointConfig;
  initialValues?: Record<string, string>;
}

interface IntakeField {
  name: string;
  label: string;
  placeholder: string;
  help?: string;
  required?: boolean;
  type?: "text" | "email" | "tel" | "url" | "textarea" | "select";
  options?: string[];
  autoComplete?: string;
}

const OptionalBadge = () => (
  <>
    <span className="sr-only"> optional</span>
    <span aria-hidden="true" className="ml-2 text-xs font-medium text-slate-500">Optional</span>
  </>
);

const baseFields: IntakeField[] = [
  {
    name: "name",
    label: "Your name",
    placeholder: "How should we address you?",
    help: "The person SVM should reply to.",
    required: true,
    autoComplete: "name",
  },
  {
    name: "email",
    label: "Work email",
    placeholder: "you@company.com",
    help: "Where we should send the next-step note.",
    required: true,
    type: "email",
    autoComplete: "email",
  },
  {
    name: "phone",
    label: "Phone",
    placeholder: "Best number for a call",
    help: "Add it only if a call would be easier.",
    type: "tel",
    autoComplete: "tel",
  },
  {
    name: "company",
    label: "Organization or project",
    placeholder: "Use the name you use now",
    help: "A working project name is fine if this is new.",
    autoComplete: "organization",
  },
  {
    name: "website",
    label: "Current website or app",
    placeholder: "https://...",
    help: "Share what exists today, if anything.",
    type: "url",
    autoComplete: "url",
  },
  {
    name: "role",
    label: "Your role",
    placeholder: "Owner, operations lead, program director...",
    help: '"I run the business" or "I manage this work" is enough.',
    autoComplete: "organization-title",
  },
  {
    name: "companyStage",
    label: "Where is the work now?",
    placeholder: "Choose the closest answer",
    help: "This helps us understand whether you are planning, building, running, or repairing.",
    type: "select",
    options: ["Exploring an idea", "Building now", "Live but early", "Growing", "Stalled or rebuilding"],
  },
  {
    name: "timeline",
    label: "When do you want to make progress?",
    placeholder: "Choose the closest answer",
    help: "A rough window is useful. This is not a delivery commitment.",
    type: "select",
    options: ["As soon as possible", "This month", "This quarter", "Later this year", "Still exploring"],
  },
  {
    name: "budgetRange",
    label: "What level of investment are you considering?",
    placeholder: "Choose one",
    help: "This helps us suggest a realistic first phase; it is not a quote.",
    type: "select",
    options: ["Not sure yet", "Under $5k", "$5k-$15k", "$15k-$50k", "$50k+"],
  },
  {
    name: "urgency",
    label: "How much is this affecting the work?",
    placeholder: "Choose the closest answer",
    help: "Use impact, not technical severity.",
    type: "select",
    options: [
      "Low - we are exploring",
      "Medium - it needs attention",
      "High - it is blocking important work",
      "Critical - customers or daily operations are affected",
    ],
  },
];

const processNotes = [
  "A person at SVM reviews your request",
  "We recommend a useful next step before proposing a build",
  "Your request stays available for private follow-up",
];

const appRescueProcessNotes = [
  "A person at SVM checks the app artifact, stack, and launch blocker",
  "We separate must-fix blockers from wishlist or polish work",
  "You get a next-step recommendation: launch, pilot, hold, rebuild, or stop",
];

const appRescueSuccessSteps = [
  "We classify the blocker and confirm whether we have enough artifact access to review it safely.",
  "If context is missing, we ask for one safe next item — usually a walkthrough, preview link, or read-only repo access.",
  "If it is a fit, the next conversation is a bounded App Rescue Sprint, not an open-ended rebuild.",
];

const appRescueBookingPrepItems = [
  "A safe preview link, walkthrough, or read-only repo access",
  "The one workflow or customer path that must work first",
  "Any launch, customer, revenue, or investor deadline",
];

const IntakeControl = ({
  field,
  value,
  onChange,
}: {
  field: IntakeField;
  value: string;
  onChange: (value: string) => void;
}) => {
  const id = `intake-${field.name}`;
  const helpId = `${id}-help`;
  const label = (
    <span className="font-bold text-slate-950">
      {field.label}
      {!field.required && <OptionalBadge />}
    </span>
  );

  return (
    <label htmlFor={id} className="grid gap-2 text-sm">
      {label}
      {field.type === "textarea" ? (
        <Textarea
          id={id}
          required={field.required}
          aria-describedby={field.help ? helpId : undefined}
          value={value}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
          className="min-h-28 resize-y"
        />
      ) : field.type === "select" ? (
        <select
          id={id}
          required={field.required}
          aria-describedby={field.help ? helpId : undefined}
          className="h-11 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rose-600 focus-visible:ring-offset-2"
          value={value}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="">{field.placeholder}</option>
          {field.options?.map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          required={field.required}
          type={field.type || "text"}
          autoComplete={field.autoComplete}
          aria-describedby={field.help ? helpId : undefined}
          value={value}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      )}
      {field.help && <span id={helpId} className="text-xs font-medium leading-5 text-slate-600">{field.help}</span>}
    </label>
  );
};

const OpportunityIntakeForm = ({ config, initialValues = {} }: OpportunityIntakeFormProps) => {
  const [form, setForm] = useState<Record<string, string>>(() => ({ ...initialValues }));
  const [payload, setPayload] = useState<OpportunityPayload | null>(null);
  const [intakeResult, setIntakeResult] = useState<OperatorIntakeResult | null>(null);
  const [intakeStep, setIntakeStep] = useState<"situation" | "contact">("situation");
  const [stepError, setStepError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consented, setConsented] = useState(false);
  // Stable for the life of this form instance so retries dedupe in Operator,
  // while a fresh page load starts a genuinely new submission.
  const submissionId = useRef(newSubmissionId());
  const hasStarted = useRef(false);
  const hasViewedContactStep = useRef(false);
  const hasTrackedConsent = useRef(false);
  const intakeSuccessRef = useRef<HTMLDivElement>(null);
  const intakeSuccessHeadingRef = useRef<HTMLHeadingElement>(null);

  const submittedResponse = intakeResult?.mode === "submitted"
    ? asSubmittedResponse(intakeResult.response)
    : null;
  const isAppRescue = config.entryPoint === "rescue_finish_app";
  const nextProcessNotes = isAppRescue ? appRescueProcessNotes : processNotes;
  const diagnosticCta = buildAppRescueDiagnosticCta(
    submittedResponse?.opportunity_id,
    import.meta.env.VITE_APP_RESCUE_DIAGNOSTIC_URL
  );

  const fieldNames = useMemo(
    () => new Set([...baseFields.map((field) => field.name), ...config.fields.map((field) => field.name)]),
    [config.fields]
  );

  const details = useMemo(() => {
    return Object.fromEntries(
      Object.entries(form).filter(([key]) => !fieldNames.has(key) && Boolean(form[key]))
    );
  }, [fieldNames, form]);

  useEffect(() => {
    if (!payload || !intakeResult) return;

    window.setTimeout(() => {
      intakeSuccessRef.current?.scrollIntoView({ block: "start" });
      intakeSuccessHeadingRef.current?.focus({ preventScroll: true });
    }, 0);
  }, [intakeResult, payload]);

  const updateField = (name: string, value: string) => {
    if (!hasStarted.current) {
      hasStarted.current = true;
      trackEvent("intake_started", buildIntakeEventProps(config));
      if (isAppRescue) {
        trackAppRescueFunnelStep("intake_started");
      }
    }
    if (name === "summary") {
      setStepError("");
    }
    setForm((current) => ({ ...current, [name]: value }));
  };

  const focusStep = () => {
    window.setTimeout(() => {
      document.getElementById("intake-step-heading")?.focus();
      document.getElementById("regular-intake")?.scrollIntoView({ block: "start" });
    }, 0);
  };

  const continueToContact = () => {
    if ((form.summary || "").trim().length < 20) {
      setStepError("Tell us a little more so we have a real situation to review.");
      window.setTimeout(() => document.getElementById("intake-summary")?.focus(), 0);
      return;
    }

    setStepError("");
    setSubmitError("");
    setIntakeStep("contact");
    trackEvent("intake_situation_completed", buildIntakeEventProps(config, {
      has_desired_outcome: Boolean(form.desiredOutcome?.trim()),
      has_path_specific_context: config.fields.some((field) => Boolean(form[field.name]?.trim())),
    }));
    if (isAppRescue) {
      const stepProps = {
        has_desired_outcome: Boolean(form.desiredOutcome?.trim()),
        has_path_specific_context: config.fields.some((field) => Boolean(form[field.name]?.trim())),
        summary_length: textLengthBucket(form.summary),
        has_app_artifact: Boolean(form.appUrl?.trim()),
        has_stack_context: Boolean(form.currentStack?.trim()),
      };
      trackAppRescueFunnelStep("situation_completed", stepProps);
      if (!hasViewedContactStep.current) {
        hasViewedContactStep.current = true;
        trackAppRescueFunnelStep("contact_viewed", stepProps);
      }
    }
    focusStep();
  };

  const returnToSituation = () => {
    setIntakeStep("situation");
    setSubmitError("");
    focusStep();
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!consented) {
      setSubmitError("Please confirm that SVM may receive these details before submitting.");
      return;
    }

    setSubmitError("");
    setIntakeResult(null);
    setIsSubmitting(true);

    const entryDetails = Object.fromEntries(
      config.fields.map((field) => [field.name, form[field.name] || ""])
    );

    const nextPayload = buildOpportunityPayload({
      config,
      form,
      details: { ...details, ...entryDetails },
      submissionId: submissionId.current,
    });

    setPayload(nextPayload);
    trackEvent("intake_submit_attempted", buildIntakeEventProps(config, {
      has_timeline: Boolean(nextPayload.request.timeline),
      has_budget_range: Boolean(nextPayload.request.budgetRange),
      has_project_translator_plan: Boolean(nextPayload.details.projectPlanRecommendation),
    }));
    if (isAppRescue) {
      trackAppRescueFunnelStep("submit_attempted", {
        has_timeline: Boolean(nextPayload.request.timeline),
        has_budget_range: Boolean(nextPayload.request.budgetRange),
        has_company: Boolean(nextPayload.contact.company),
        has_website: Boolean(nextPayload.business.website),
        has_app_artifact: Boolean(nextPayload.details.appUrl?.trim()),
        has_stack_context: Boolean(nextPayload.details.currentStack?.trim()),
        urgency: nextPayload.request.urgency.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || undefined,
        qualification_score: nextPayload.qualification.score ?? undefined,
      });
    }

    try {
      const result = await submitOperatorOpportunity(nextPayload);
      setIntakeResult(result);
      const submitted = result.mode === "submitted" ? asSubmittedResponse(result.response) : null;
      trackEvent(
        "intake_submitted",
        buildIntakeSubmissionProps(config, nextPayload, result.mode, submitted?.duplicate)
      );
      if (isAppRescue) {
        trackAppRescueFunnelStep("operator_opportunity_created", {
          mode: result.mode,
          duplicate: submitted?.duplicate,
          operator_status: submitted?.status,
          qualification_score: nextPayload.qualification.score ?? undefined,
        });
      }
    } catch (error) {
      trackEvent("intake_failed", buildIntakeEventProps(config, { reason: "submit_error" }));
      if (isAppRescue) {
        trackAppRescueFunnelStep("submit_failed", { reason: "submit_error" });
      }
      setSubmitError(
        error instanceof Error
          ? error.message
          : "We could not send this intake right now. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDiagnosticBookingClick = () => {
    if (!isAppRescue || intakeResult?.mode !== "submitted") return;

    trackAppRescueFunnelStep("diagnostic_booking_clicked", {
      mode: intakeResult.mode,
      duplicate: submittedResponse?.duplicate,
      operator_status: submittedResponse?.status,
      has_reference: Boolean(submittedResponse?.opportunity_id),
      placement: "app_rescue_success_primary",
      booking_mode: diagnosticCta.mode,
    });
  };

  return (
    <section id="opportunity-intake" className="svm-ink-grid scroll-mt-20 bg-[#080b0f] py-12 text-white md:py-16">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-[0.82fr_1.18fr] lg:gap-14">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <p className="mb-3 inline-flex rounded-full border border-emerald-300/30 bg-emerald-300/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-200">
              {config.eyebrow}
            </p>
            <h2 className="svm-display-tight mb-4 text-4xl leading-[0.95] md:text-5xl">{config.title}</h2>
            <p className="max-w-xl font-medium leading-7 text-slate-300">
              {config.description} Share what you know in ordinary language. We will recommend a practical next step.
            </p>

            <div className="mt-8 flex gap-3 border-y border-white/15 py-5 text-sm font-medium leading-6 text-slate-300">
              <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
              <p><strong className="text-white">No technical plan required.</strong> A link, rough description, or current workflow is enough to begin.</p>
            </div>

            <div className="mt-7">
              <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">What happens next</p>
              {nextProcessNotes.map((note, index) => (
                <div key={note} className="grid grid-cols-[30px_1fr] gap-3 border-t border-white/15 py-3 first:border-t-0">
                  <span className="font-mono text-xs font-bold text-amber-300">0{index + 1}</span>
                  <span className="text-sm font-semibold text-slate-200">{note}</span>
                </div>
              ))}
            </div>
          </div>

          <div id="regular-intake" className="scroll-mt-24 rounded-md border border-white/10 bg-[#fffdf6] p-5 text-slate-950 shadow-[0_30px_90px_rgba(0,0,0,0.38)] md:p-8">
            {payload && intakeResult ? (
              <div ref={intakeSuccessRef} aria-live="polite" className="flex min-h-[430px] scroll-mt-28 flex-col justify-center">
                <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-800">
                  <CheckCircle2 className="h-7 w-7" />
                </span>
                <p className="mb-3 text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">
                  {intakeResult.mode === "submitted" ? "Request received" : "Preview ready"}
                </p>
                <h3 ref={intakeSuccessHeadingRef} tabIndex={-1} className="svm-display-tight text-4xl outline-none">
                  {intakeResult.mode === "submitted"
                    ? submittedResponse?.duplicate
                      ? "We found your existing request."
                      : "Your request is with SVM."
                    : "Your intake is prepared."}
                </h3>
                <p className="mt-4 max-w-xl font-medium leading-7 text-slate-700">
                  {intakeResult.mode === "submitted"
                    ? submittedResponse?.duplicate
                      ? "We will review these details with the earlier request and follow up from the same record."
                      : "A person at SVM will review the context and recommend what should happen next."
                    : intakeResult.message}
                </p>
                {submittedResponse?.opportunity_id && (
                  <p className="mt-5 text-sm font-bold text-slate-700">Reference: {submittedResponse.opportunity_id}</p>
                )}
                {isAppRescue && intakeResult.mode === "submitted" && (
                  <div className="mt-7 grid gap-4">
                    <div className="rounded-md border border-rose-200 bg-rose-50 p-5">
                      <p className="mb-2 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">Next action</p>
                      <h4 className="svm-display-tight text-2xl leading-tight text-slate-950">Book the diagnostic while the context is fresh.</h4>
                      <p className="mt-3 text-sm font-medium leading-6 text-slate-700">
                        {diagnosticCta.mode === "scheduler"
                          ? "Choose a diagnostic slot and we will turn this intake into a bounded review instead of an open-ended rebuild thread."
                          : "Send two workable call windows and we will turn this intake into a bounded review instead of an open-ended rebuild thread."}
                      </p>
                      <Button asChild size="lg" className="mt-5 w-full gap-2 rounded-md bg-slate-950 font-bold text-white hover:bg-rose-700 sm:w-auto">
                        <a
                          href={diagnosticCta.href}
                          onClick={handleDiagnosticBookingClick}
                          target={diagnosticCta.opensNewTab ? "_blank" : undefined}
                          rel={diagnosticCta.opensNewTab ? "noopener noreferrer" : undefined}
                        >
                          <CalendarCheck className="h-4 w-4" />
                          {diagnosticCta.label}
                        </a>
                      </Button>
                      <p className="mt-3 text-xs font-semibold leading-5 text-slate-600">
                        Do not send passwords or credentials. Safe preview links and read-only access can wait for the reply.
                      </p>
                    </div>

                    <div className="rounded-md border border-slate-200 bg-white p-5">
                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-rose-700">App Rescue triage</p>
                      <div className="grid gap-3">
                        {appRescueSuccessSteps.map((step, index) => (
                          <div key={step} className="grid grid-cols-[28px_1fr] gap-3 text-sm font-semibold leading-6 text-slate-700">
                            <span className="font-mono text-xs font-bold text-slate-500">0{index + 1}</span>
                            <span>{step}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="rounded-md border border-slate-200 bg-slate-50 p-5">
                      <p className="mb-3 text-xs font-bold uppercase tracking-[0.16em] text-slate-500">Have ready for the call</p>
                      <ul className="grid gap-2 text-sm font-semibold leading-6 text-slate-700">
                        {appRescueBookingPrepItems.map((item) => (
                          <li key={item} className="flex gap-2">
                            <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                {intakeResult.mode === "local_preview" && (
                  <details className="mt-7 border-t border-slate-300 pt-5">
                    <summary className="cursor-pointer text-sm font-bold text-slate-900">Review prepared Operator payload</summary>
                    <pre className="mt-3 max-h-72 overflow-auto rounded-md bg-slate-950 p-4 text-xs text-slate-100">
                      {JSON.stringify(intakeResult.request ?? payload, null, 2)}
                    </pre>
                  </details>
                )}
                <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm font-bold text-slate-950 underline decoration-amber-400 decoration-2 underline-offset-4 hover:text-rose-700">
                  Return to the SVM home page
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            ) : (
              <form className="grid gap-6" onSubmit={handleSubmit}>
                <label className="hidden">
                  Company website
                  <Input
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.hp_company || ""}
                    onChange={(event) => updateField("hp_company", event.target.value)}
                  />
                </label>

                <div>
                  <div className="mb-3 flex items-center justify-between text-xs font-bold uppercase tracking-[0.14em] text-slate-500">
                    <span>Step {intakeStep === "situation" ? 1 : 2} of 2</span>
                    <span>{intakeStep === "situation" ? "The situation" : "Contact and send"}</span>
                  </div>
                  <div
                    className="grid grid-cols-2 gap-2"
                    role="progressbar"
                    aria-label={`Step ${intakeStep === "situation" ? 1 : 2} of 2`}
                    aria-valuemin={1}
                    aria-valuemax={2}
                    aria-valuenow={intakeStep === "situation" ? 1 : 2}
                  >
                    <span aria-hidden="true" className="h-1 bg-rose-700" />
                    <span aria-hidden="true" className={`h-1 ${intakeStep === "contact" ? "bg-rose-700" : "bg-slate-200"}`} />
                  </div>
                </div>

                {intakeStep === "situation" ? (
                  <div className="grid gap-6">
                    <div>
                      <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">Start here</p>
                      <h3 id="intake-step-heading" tabIndex={-1} className="svm-display-tight text-3xl outline-none md:text-4xl">Describe the situation.</h3>
                      <p className="mt-3 text-sm font-medium leading-6 text-slate-600">What happens, who is affected, and where does it get hard? You do not need to know the solution.</p>
                    </div>

                    <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm font-semibold leading-6 text-amber-950">
                      Share only details about the work. Do not include passwords, credentials, private records, or regulated data.
                    </div>

                    <label htmlFor="intake-summary" className="grid gap-2 text-sm">
                      <span className="font-bold text-slate-950">What is happening right now?</span>
                      <Textarea
                        id="intake-summary"
                        required
                        aria-describedby="intake-summary-help"
                        aria-invalid={Boolean(stepError)}
                        value={form.summary || ""}
                        placeholder="Describe the situation as you would explain it to a trusted colleague."
                        onChange={(event) => updateField("summary", event.target.value)}
                        className="min-h-36 resize-y"
                      />
                      <span id="intake-summary-help" className="text-xs font-medium leading-5 text-slate-600">A few useful sentences are enough.</span>
                    </label>

                    <label htmlFor="intake-desired-outcome" className="grid gap-2 text-sm">
                      <span className="font-bold text-slate-950">What should be easier when this works? <OptionalBadge /></span>
                      <Textarea
                        id="intake-desired-outcome"
                        aria-describedby="intake-desired-outcome-help"
                        value={form.desiredOutcome || ""}
                        placeholder="Describe the change you want people, customers, or the business to feel."
                        onChange={(event) => updateField("desiredOutcome", event.target.value)}
                        className="min-h-24 resize-y"
                      />
                      <span id="intake-desired-outcome-help" className="text-xs font-medium leading-5 text-slate-600">A clear result is more useful than a feature list.</span>
                    </label>

                    <details className="rounded-md border border-slate-200 bg-white p-4">
                      <summary className="cursor-pointer font-bold text-slate-950">
                        Add path-specific context <OptionalBadge />
                      </summary>
                      <p className="mt-2 text-xs font-medium leading-5 text-slate-600">These details help us prepare, but you can continue without them.</p>
                      <div className="mt-5 grid gap-5">
                        <label htmlFor="intake-pain-points" className="grid gap-2 text-sm">
                          <span className="font-bold text-slate-950">Where does it go wrong? <OptionalBadge /></span>
                          <Textarea
                            id="intake-pain-points"
                            aria-describedby="intake-pain-points-help"
                            value={form.painPoints || ""}
                            placeholder="For example: requests are missed, people repeat work, customers wait, or sign-in fails."
                            onChange={(event) => updateField("painPoints", event.target.value)}
                            className="min-h-24 resize-y"
                          />
                          <span id="intake-pain-points-help" className="text-xs font-medium leading-5 text-slate-600">Describe what you observe. SVM can investigate the cause.</span>
                        </label>
                        {config.fields.map((field) => (
                          <IntakeControl
                            key={field.name}
                            field={{ ...field, type: field.type || "text" }}
                            value={form[field.name] || ""}
                            onChange={(value) => updateField(field.name, value)}
                          />
                        ))}
                      </div>
                    </details>

                    {stepError && <p role="alert" className="border-l-4 border-red-600 bg-red-50 px-4 py-3 text-sm font-bold text-red-800">{stepError}</p>}

                    <Button type="button" size="lg" onClick={continueToContact} className="w-full gap-2 rounded-md bg-slate-950 font-bold text-white hover:bg-rose-700 sm:w-auto sm:justify-self-end">
                      Continue to contact details
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="grid gap-6">
                    <div>
                      <p className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-rose-700">Almost done</p>
                      <h3 id="intake-step-heading" tabIndex={-1} className="svm-display-tight text-3xl outline-none md:text-4xl">Where should we follow up?</h3>
                      <p className="mt-3 text-sm font-medium leading-6 text-slate-600">A person at SVM reviews every request before recommending work.</p>
                    </div>

                    <div className="border-l-4 border-amber-300 bg-amber-50 p-4">
                      <p className="text-xs font-bold uppercase tracking-[0.14em] text-amber-900">Your situation</p>
                      <p className="mt-2 line-clamp-3 text-sm font-medium leading-6 text-slate-700">{form.summary}</p>
                      <button type="button" onClick={returnToSituation} className="mt-3 text-sm font-bold text-slate-950 underline underline-offset-4 hover:text-rose-700">Edit this</button>
                    </div>

                    <div className="grid gap-5 md:grid-cols-2">
                      {baseFields.slice(0, 2).map((field) => (
                        <IntakeControl
                          key={field.name}
                          field={field}
                          value={form[field.name] || ""}
                          onChange={(value) => updateField(field.name, value)}
                        />
                      ))}
                      <div className="md:col-span-2">
                        <IntakeControl
                          field={baseFields[3]}
                          value={form.company || ""}
                          onChange={(value) => updateField("company", value)}
                        />
                      </div>
                    </div>

                    <details className="rounded-md border border-slate-200 bg-white p-4">
                      <summary className="cursor-pointer font-bold text-slate-950">
                        Add timing and background <OptionalBadge />
                      </summary>
                      <p className="mt-2 text-xs font-medium leading-5 text-slate-600">Useful for the first conversation, but not required to send.</p>
                      <div className="mt-5 grid gap-5 md:grid-cols-2">
                        {[baseFields[2], baseFields[4], ...baseFields.slice(5)].map((field) => (
                          <IntakeControl
                            key={field.name}
                            field={field}
                            value={form[field.name] || ""}
                            onChange={(value) => updateField(field.name, value)}
                          />
                        ))}
                        <label htmlFor="intake-current-tools" className="grid gap-2 text-sm md:col-span-2">
                          <span className="font-bold text-slate-950">What tools are already part of the work? <OptionalBadge /></span>
                          <Input
                            id="intake-current-tools"
                            aria-describedby="intake-current-tools-help"
                            value={form.currentTools || ""}
                            placeholder="For example: Gmail, spreadsheets, HubSpot, Slack, Stripe, or 'not sure'"
                            onChange={(event) => updateField("currentTools", event.target.value)}
                          />
                          <span id="intake-current-tools-help" className="text-xs font-medium leading-5 text-slate-600">Name what people actually use today.</span>
                        </label>
                      </div>
                    </details>

                    <label className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-4 text-sm font-medium leading-6 text-slate-700">
                      <input
                        type="checkbox"
                        required
                        checked={consented}
                        onChange={(event) => {
                          setConsented(event.target.checked);
                          if (event.target.checked) {
                            setSubmitError("");
                            trackEvent("intake_consent_checked", buildIntakeEventProps(config));
                            if (isAppRescue && !hasTrackedConsent.current) {
                              hasTrackedConsent.current = true;
                              trackAppRescueFunnelStep("consent_checked");
                            }
                          }
                        }}
                        className="mt-1 h-4 w-4 shrink-0 rounded border-slate-400 text-rose-600 focus:ring-rose-500"
                      />
                      <span>
                        I consent to Shock Value Media receiving these details, including any Project Translator draft, so a person can review it and follow up. See the{" "}
                        <Link to="/privacy" className="font-bold underline hover:text-slate-950">privacy policy</Link>.
                      </span>
                    </label>

                    {submitError && (
                      <div aria-live="polite" className="rounded-md border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800">{submitError}</div>
                    )}

                    <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-between">
                      <Button type="button" variant="ghost" onClick={returnToSituation} disabled={isSubmitting} className="gap-2 rounded-md">
                        <ArrowLeft className="h-4 w-4" />
                        Back
                      </Button>
                      <Button type="submit" size="lg" disabled={isSubmitting || !consented} className="gap-2 rounded-md bg-slate-950 font-bold text-white shadow-[0_18px_40px_rgba(15,23,42,0.2)] hover:bg-rose-600 disabled:opacity-60">
                        <Send className="h-4 w-4" />
                        {isSubmitting ? "Sending..." : config.primaryCta}
                      </Button>
                    </div>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default OpportunityIntakeForm;
